import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaCheck, FaCaretDown } from "react-icons/fa";
import { AssistantMode } from "@/models/conversation";
import { ChatTheme, ModeConfig } from "./types";
import { ASSISTANT_MODES } from "./chat-constants";

// ============================================================================
// Props
// ============================================================================

interface ModeSelectorProps {
  mode: AssistantMode;
  onModeChange: (mode: AssistantMode) => void;
  disabled?: boolean;
  theme: ChatTheme;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Dropdown selector for assistant mode (Agent/Ask/Plan).
 */
export const ModeSelector: React.FC<ModeSelectorProps> = ({
  mode,
  onModeChange,
  disabled = false,
  theme,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const currentModeConfig = ASSISTANT_MODES.find((m) => m.id === mode) || ASSISTANT_MODES[0];

  // Close menu on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleSelect = (selectedMode: AssistantMode) => {
    onModeChange(selectedMode);
    setIsOpen(false);
  };

  const anchorRect = anchorRef.current?.getBoundingClientRect();

  const dropdownMenu =
    isOpen && anchorRect && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={menuRef}
            className="fixed"
            style={{
              top: anchorRect.top - 8 - (ASSISTANT_MODES.length * 52 + 8),
              left: anchorRect.left,
              zIndex: 99999,
            }}
          >
            <div
              className={`
                rounded-lg border py-1 min-w-[200px] shadow-xl backdrop-blur-sm
                ${theme.bg} ${theme.border}
                animate-in fade-in-0 zoom-in-95 duration-100
              `}
            >
              {ASSISTANT_MODES.map((modeConfig: ModeConfig) => (
                <button
                  key={modeConfig.id}
                  onClick={() => handleSelect(modeConfig.id)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2 text-sm
                    transition-colors duration-100
                    ${
                      mode === modeConfig.id
                        ? `${modeConfig.textColor} ${modeConfig.bgColor}`
                        : `${theme.textSecondary} hover:${theme.textPrimary} hover:bg-white/5`
                    }
                  `}
                >
                  <span className={`flex-shrink-0 ${modeConfig.color}`}>
                    {modeConfig.icon}
                  </span>
                  <div className="flex flex-col items-start gap-0.5">
                    <span className="font-medium">{modeConfig.label}</span>
                    <span className={`text-xs ${theme.textMuted}`}>
                      {modeConfig.description}
                    </span>
                  </div>
                  {mode === modeConfig.id && (
                    <FaCheck
                      className={`ml-auto h-3 w-3 ${modeConfig.color} flex-shrink-0`}
                    />
                  )}
                </button>
              ))}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <button
        ref={anchorRef}
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium
          transition-all duration-150 flex-shrink-0
          ${currentModeConfig.bgColor} hover:opacity-80
          ${currentModeConfig.textColor}
          disabled:opacity-50 disabled:cursor-not-allowed
        `}
        title={currentModeConfig.description}
      >
        <span className={currentModeConfig.color}>{currentModeConfig.icon}</span>
        <span className="hidden sm:inline">{currentModeConfig.label}</span>
        <FaCaretDown
          className={`h-2.5 w-2.5 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      {dropdownMenu}
    </>
  );
};

ModeSelector.displayName = "ModeSelector";

export default ModeSelector;
